// packages
import React from "react";
import { useWindowSize } from "@react-hookz/web";
// components
import PreventiveCard from "./PreventiveCard";
// utils
import { IconUtils } from "@/shared/utils/IconUtils";
import { PreventiveCoverageType, PreventiveOption, PreventiveConfig } from "@/shared/utils/CoverageUtils";
// media
import GoldPreventive from "@/shared/media/images/gold-preventive.svg";
import PlatinumPreventive from "@/shared/media/images/platinum-preventive.svg";
import GoldPreventiveRebrand from "@/shared/media/images/gold-preventive-rebrand.svg";
import PlatinumPreventiveRebrand from "@/shared/media/images/platinum-preventive-rebrand.svg";
// types
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { Carousel, CarouselContent, CarouselItem, CarouselNavDots } from "@/shared/components/ui/carousel";
import { Pet, Quote } from "@/shared/types/Quote.interface";
import { useFormParentContext } from "@/shared/contexts/FormParent";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { SectionGridItem } from "./SectionGrid";
import { IconGridItem } from "@/shared/types/IconGrid.interface";
import { useCoverageEditorContextSafe } from "../CoverageEditor";
import { useThemeContext } from "@/shared/contexts/ThemeProvider";
import { cn } from "@/shared/utils";

interface PreventiveIconGridProps {
    pet: Pet;
    preventiveOptions: PreventiveOption[];
    preventiveConfig: PreventiveConfig;
    underwriter: PetUnderwriterType;
    wellnessLevel: string;
    handleChange: (coverageLevel: PreventiveCoverageType) => void;
    showNumbered?: boolean;
    showPlatinumRecommendation?: boolean;
    isCategorizedPreventiveEnabled?: boolean;
    showClickableCards?: boolean;
}

export function PreventiveCards({
    pet,
    preventiveOptions,
    preventiveConfig,
    underwriter,
    wellnessLevel,
    handleChange,
    showPlatinumRecommendation,
    isCategorizedPreventiveEnabled: isCategorizedPreventiveProp,
    showNumbered,
    showClickableCards
}: PreventiveIconGridProps) {
    const { showRebrandTheme } = useThemeContext().theme;
    const goldLevel = preventiveOptions.find((option: PreventiveOption) => option.value === "wellness-gold");
    const platinumLevel = preventiveOptions.find((option: PreventiveOption) => option.value === "wellness-platinum");

    const { watch } = useFormParentContext<Quote>();
    const quote = watch();

    const { activePetIndex, isApplyAllHidden, isMultiPetLinked } = useAppLayerContext().appState;

    const editorContext = useCoverageEditorContextSafe();
    const isCategorizedPreventiveEnabled = isCategorizedPreventiveProp ?? editorContext?.isCategorizedPreventiveEnabled;

    const hasBreedRules = IconUtils.hasBreedPreventiveRules(pet.breedName);

    // The pet rendered here is not always the active one: the billing summary's preventive modal opens
    // for whichever pet's row was clicked without touching `activePetIndex`. IconUtils reads the breed
    // rules and the dog/cat item text off `quote.policies[activePetIndex]`, so resolve this pet's own
    // index or the badge (from `pet`) and the highlighted items (from the active pet) describe
    // different animals. Falls back to the active index for a pet that has no id yet.
    const petIndex = quote?.policies?.findIndex(policy => !!policy.id && policy.id === pet.id) ?? -1;

    const benefitsParams = {
        petAge: pet.age,
        quote: quote,
        activePetIndex: petIndex >= 0 ? petIndex : activePetIndex,
        isApplyAllHidden: isApplyAllHidden,
        applyToAllPets: isMultiPetLinked,
        showRebrandIcon: showRebrandTheme,
        applyBreedRecs: hasBreedRules
    };

    const goldBenefits = isCategorizedPreventiveEnabled
        ? IconUtils.getPreventiveBenefitsBySection(underwriter, benefitsParams, "gold", showRebrandTheme)
        : IconUtils.getPreventiveGoldBenefits(underwriter, benefitsParams);

    const platinumBenefits = isCategorizedPreventiveEnabled
        ? IconUtils.getPreventiveBenefitsBySection(underwriter, benefitsParams, "platinum", showRebrandTheme)
        : IconUtils.getPreventivePlatinumBenefits(underwriter, benefitsParams);

    const totalGoldBenefits = isCategorizedPreventiveEnabled
        ? goldBenefits.reduce((acc, section) => {
              // Sum up all amounts in the items array of each section
              const sectionTotal = (section as SectionGridItem).items.reduce((itemAcc, item) => itemAcc + (item.amount || 0), 0);
              return acc + sectionTotal;
          }, 0)
        : goldBenefits.reduce((acc, item) => acc + ((item as IconGridItem).amount || 0), 0);

    const { width } = useWindowSize();

    const goldPreventiveImage = showRebrandTheme ? <GoldPreventiveRebrand className="size-16" /> : <GoldPreventive className="size-16" />;
    const platinumPreventiveImage = showRebrandTheme ? <PlatinumPreventiveRebrand className="w-[68px]" /> : <PlatinumPreventive className="h-[80px] w-[68px]" />;

    return (
        <Carousel disabled={width >= 768}>
            {/*pb-2 needed for Card drop-shadow*/}
            <CarouselContent className={cn("flex pb-2", showNumbered && "relative z-10 gap-2 md:gap-0")} contentWrapperClass="-mx-5 px-4 sm:-mx-6 sm:px-6 md:mx-0 md:px-0">
                <CarouselItem className="basis-[99%] pl-0 md:order-2 md:basis-1/2 md:pl-4">
                    <PreventiveCard
                        pet={pet}
                        price={platinumLevel?.price}
                        label={preventiveConfig.labels.advanced}
                        image={platinumPreventiveImage}
                        benefits={platinumBenefits}
                        currentWellnessLevel={wellnessLevel}
                        wellnessLevel={"wellness-platinum"}
                        onChange={handleChange}
                        sellingPoints={["More reimbursements", "More benefits"]}
                        showPopularAge={true}
                        columnClasses="grid-cols-2"
                        headerClasses="items-end lg:items-center"
                        categorizePreventive={isCategorizedPreventiveEnabled}
                        showClickableCards={showClickableCards}
                    />
                </CarouselItem>
                <CarouselItem className="basis-[99%] pl-1 md:order-1 md:basis-1/2 md:pl-0">
                    <PreventiveCard
                        pet={pet}
                        price={goldLevel?.price}
                        label={preventiveConfig.labels.basic}
                        image={goldPreventiveImage}
                        benefits={goldBenefits}
                        currentWellnessLevel={wellnessLevel}
                        wellnessLevel={"wellness-gold"}
                        onChange={handleChange}
                        totalBenefitsValue={totalGoldBenefits}
                        columnClasses="grid-cols-2 md:grid-cols-1 xl:grid-cols-2"
                        headerClasses="items-center"
                        categorizePreventive={isCategorizedPreventiveEnabled}
                        showPlatinumBanner={showPlatinumRecommendation}
                        showClickableCards={showClickableCards}
                    />
                </CarouselItem>
            </CarouselContent>
            <CarouselNavDots className="mt-4 md:hidden" />
        </Carousel>
    );
}
